import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { DisplayDateFormat, formatDate, toISOFormat } from '@northfork/basics'
import { ErrorMessage } from '@northfork/ng-basics'

/**
 * FormControl validator to check if the form control value exceeds the hold limit in days.
 */
export function HoldExpirationDaysValidator(daysLimit: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value == undefined) {
      return null
    }

    if (control.value < 0) {
      return {
        HoldExpirationNegativeDays: {},
      }
    }

    if (control.value > daysLimit) {
      return {
        HoldExpirationExceedsLimitInDays: {
          daysLimit,
        },
      }
    }
    return null
  }
}

/**
 * FormControl validator to check if the hold expiration date form control value exceeds the hold limit by date.
 */
export function HoldExpirationDateValidator(
  currentDate: Date,
  dateLimit: Date,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value == undefined) {
      return null
    }
    const expirationDate = control.value as Date
    const currentDateISO = toISOFormat(currentDate)
    const expirationDateISO = toISOFormat(expirationDate)
    const dateLimitISO = toISOFormat(dateLimit)
    if (currentDateISO == null) {
      throw new Error(
        `HoldExpirationDateValidator: ISO formatted expiration date is null: ${currentDate}`,
      )
    }
    if (expirationDateISO == null) {
      throw new Error(
        `HoldExpirationDateValidator: ISO formatted expiration date is null: ${expirationDate}`,
      )
    }
    if (dateLimitISO == null) {
      throw new Error(
        `HoldExpirationDateValidator: ISO formatted date limit is null: ${dateLimit}`,
      )
    }
    if (currentDateISO > expirationDateISO) {
      return {
        HoldExpirationDateInPast: {},
      }
    }
    if (expirationDateISO > dateLimitISO) {
      return {
        HoldExpirationDateBeyondDateLimit: {
          dateLimit: formatDate(dateLimit, DisplayDateFormat),
        },
      }
    }
    return null
  }
}

const holdTimeValidationMessages: ErrorMessage[] = [
  {
    error: 'HoldExpirationNegativeDays',
    format: (label, error): string => {
      return `Must not be negative.`
    },
  },
  {
    error: 'HoldExpirationExceedsLimitInDays',
    format: (label, error): string => {
      return `Must not exceed limit of ${error.daysLimit} days.`
    },
  },
  {
    error: 'HoldExpirationDateInPast',
    format: (): string => {
      return `Must not be in the past.`
    },
  },
  {
    error: 'HoldExpirationDateBeyondDateLimit',
    format: (label, error): string => {
      return `Must not be beyond ${error.dateLimit}.`
    },
  },
]

export const HoldModalValidationMessages = {
  holdTimeValidationMessages,
}

/**
 * First human-readable message for a hold-expiry control's errors (the
 * required check plus the hold-time rules). Used to surface why Save is
 * disabled when the inline message is hidden (compact controls).
 */
export function firstHoldTimeErrorMessage(errors: ValidationErrors): string {
  if (errors['required']) {
    return 'An expiration is required.'
  }
  for (const m of holdTimeValidationMessages) {
    if (errors[m.error]) {
      return m.format('', errors[m.error])
    }
  }
  return 'Enter a valid expiration.'
}
